import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, ArrowDownToLine, ArrowUpFromLine, Edit2 } from "lucide-react";
import { toast } from "sonner";
import StockMovementModal from "@/components/StockMovementModal";
import ProductForm from "@/components/ProductForm";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [movs, setMovs] = useState([]);
  const [cats, setCats] = useState([]);
  const [movOpen, setMovOpen] = useState(false);
  const [movType, setMovType] = useState("IN");
  const [editOpen, setEditOpen] = useState(false);

  async function load() {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/stock-movements`, { params: { product_id: id, limit: 100 } }),
        api.get(`/categories`),
      ]);
      setP(r1.data); setMovs(r2.data); setCats(r3.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      nav("/products");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!p) return <div className="p-10 font-mono-ds text-xs uppercase tracking-widest">Loading…</div>;

  const cat = cats.find((c) => c.id === p.category_id);

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="product-detail-page">
      <button onClick={() => nav("/products")} className="brut-btn-outline px-3 py-2 inline-flex items-center gap-2 text-xs mb-6" data-testid="back-btn">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div>
          <div className="aspect-square brut-card overflow-hidden bg-zinc-100">
            {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full brut-stripes opacity-10" />}
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {p.images.slice(1).map((path) => (
                <div key={path} className="aspect-square brut-card-flat overflow-hidden bg-zinc-100">
                  <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">{cat?.name || "UNCATEGORIZED"}</div>
          <h1 className="section-title text-4xl lg:text-5xl mb-2">{p.name}</h1>
          <div className="font-mono-ds text-sm text-zinc-600 mb-4">{p.sku}</div>

          <div className="flex gap-2 mb-5">
            {p.total_stock === 0 && <span className="brut-badge alert">OUT OF STOCK</span>}
            {p.total_stock > 0 && p.is_low_stock && <span className="brut-badge warning">LOW STOCK</span>}
            {p.total_stock > 0 && !p.is_low_stock && <span className="brut-badge success">IN STOCK</span>}
            {p.color && <span className="brut-badge">{p.color}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="brut-card-flat p-4">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">PRICE</div>
              <div className="font-mono-ds font-bold text-2xl">₹{Number(p.price).toLocaleString("en-IN")}</div>
            </div>
            <div className="brut-card-flat p-4">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">TOTAL STOCK</div>
              <div className="font-mono-ds font-bold text-2xl">{p.total_stock}</div>
            </div>
          </div>

          {p.description && <p className="text-sm text-zinc-700 mb-5">{p.description}</p>}

          <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-2">Size-wise stock</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
            {p.sizes?.map((s) => {
              const thr = s.effective_threshold ?? p.low_stock_threshold;
              const out = s.stock <= 0;
              const low = !out && s.stock <= thr;
              return (
                <div key={s.size} className={`brut-card-flat p-3 text-center ${out ? "bg-[#FF3333] text-white" : low ? "bg-[#FFB800]" : ""}`} data-testid={`size-stock-${s.size}`}>
                  <div className="font-display font-black">{s.size}</div>
                  <div className="font-mono-ds font-bold text-lg">{s.stock}</div>
                  <div className="font-mono-ds text-[9px] tracking-widest uppercase opacity-70 mt-1">thr {thr}</div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2">
            <button data-testid="stock-in-btn" onClick={() => { setMovType("IN"); setMovOpen(true); }} className="brut-btn px-4 py-2.5 inline-flex items-center gap-2 bg-[#00C853] text-black border-black">
              <ArrowDownToLine size={16} /> Stock In
            </button>
            <button data-testid="stock-out-btn" onClick={() => { setMovType("OUT"); setMovOpen(true); }} className="brut-btn px-4 py-2.5 inline-flex items-center gap-2 bg-[#FF3333] border-black">
              <ArrowUpFromLine size={16} /> Stock Out
            </button>
            <button data-testid="edit-product-btn" onClick={() => setEditOpen(true)} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2">
              <Edit2 size={16} /> Edit
            </button>
          </div>
        </div>
      </div>

      <div className="brut-card p-6">
        <h2 className="section-title text-2xl mb-4">Movement History</h2>
        <table className="brut-table">
          <thead><tr><th>When</th><th>Type</th><th>Size</th><th>Qty</th><th>Reason</th><th>By</th><th>Notes</th></tr></thead>
          <tbody>
            {movs.map((m) => (
              <tr key={m.id}>
                <td className="font-mono-ds text-xs">{new Date(m.created_at).toLocaleString()}</td>
                <td>{m.type === "IN" ? <span className="brut-badge success">IN</span> : <span className="brut-badge alert">OUT</span>}</td>
                <td className="font-mono-ds">{m.size}</td>
                <td className="font-mono-ds font-bold">{m.quantity}</td>
                <td className="font-mono-ds text-xs">{m.reason}</td>
                <td className="text-xs">{m.user_name}</td>
                <td className="text-xs">{m.notes}</td>
              </tr>
            ))}
            {movs.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 font-mono-ds text-xs uppercase">No history yet</td></tr>}
          </tbody>
        </table>
      </div>

      {movOpen && <StockMovementModal product={p} type={movType} onClose={() => setMovOpen(false)} onSaved={() => { setMovOpen(false); load(); }} />}
      {editOpen && <ProductForm product={p} categories={cats} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
    </div>
  );
}
